/**
 * O que faz: "gaveta" que abre e fecha deslizando — o espaço cresce suave e o conteúdo desce com fade.
 *            Padrão do projeto para qualquer área que expande dentro da página (linha que abre detalhes,
 *            opções avançadas, histórico com payload).
 * Onde usa: Conversões Meta (etapas, política própria, importação offline, histórico de envios) e
 *           qualquer nova área expansível (regra em AGENTS.md).
 * Conexões: utilitário cn. Não usa Radix nem estado próprio: quem abre/fecha é o componente pai.
 * Tabelas: nenhuma.
 * Data: 07/10/2026
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface GavetaSuaveProps {
  aberta: boolean;
  children: ReactNode;
  id?: string;
  className?: string;
}

/**
 * Por que grid-template-rows (0fr → 1fr): é o jeito de animar até a altura real do conteúdo sem medir
 * nada em JavaScript. É a única exceção à regra "só transform e opacity": a gaveta precisa empurrar o
 * conteúdo de baixo, senão ele "pula". O conteúdo em si entra com transform + opacity.
 * Movimento reduzido: abre e fecha na hora, sem transição.
 */
export function GavetaSuave({ aberta, children, id, className }: GavetaSuaveProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fechada, a gaveta continua no DOM (para animar o fechamento), mas sai do Tab e do leitor de tela.
    if (!ref.current) return;
    if (aberta) ref.current.removeAttribute('inert');
    else ref.current.setAttribute('inert', '');
  }, [aberta]);

  return (
    <div
      ref={ref}
      id={id}
      aria-hidden={!aberta}
      className={cn(
        'grid transition-[grid-template-rows,opacity] duration-[240ms] ease-out motion-reduce:transition-none',
        aberta ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          className={cn(
            'transition-transform duration-[240ms] ease-out motion-reduce:transition-none',
            aberta ? 'translate-y-0' : '-translate-y-2',
            className,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
